import { useEffect, useState } from 'react'

/**
 * LIVE CHAT CONNECTIONS. A WebSocket that reconnects on its own, and the one the staff
 * app shares between the top bar, the inbox and the open conversation.
 *
 * The server only PUSHES over these (new messages, status, typing). Sending still goes
 * over HTTP, and every screen still polls, slowly while connected and quickly while not,
 * so nothing depends on a socket staying up. See dist/services/chatSocket.js.
 */
export type LiveEvent =
  | { type: 'ready'; conversations?: string[] }
  | { type: 'message'; conversationId: string; messages: unknown[]; status?: string; with?: string | null; conversation?: unknown }
  | { type: 'typing'; conversationId: string; who: string; side?: 'staff' | 'patient' }

export interface Live {
  send: (o: object) => void
  close: () => void
  isOpen: () => boolean
  /** Gave up for good (refused); make a new one to try again. */
  isClosed: () => boolean
  on: (fn: (e: LiveEvent) => void) => () => void
  onStatus: (fn: (open: boolean) => void) => () => void
  /** Say hello again (a new conversation to listen to). */
  rehello: () => void
}

const wsUrl = (path: string) => `${location.protocol === 'https:' ? 'wss' : 'ws'}://${location.host}${path}`

/**
 * @param path    the socket's address on this host
 * @param hello   the first frame, sent on every (re)connect; null means "nothing to say yet"
 */
export function connectLive(path: string, hello: () => object | null): Live {
  const listeners = new Set<(e: LiveEvent) => void>()
  const statusListeners = new Set<(open: boolean) => void>()
  let ws: WebSocket | null = null
  let open = false
  let closed = false
  let wait = 1000
  let timer: ReturnType<typeof setTimeout> | null = null

  const setOpen = (v: boolean) => { if (open !== v) { open = v; statusListeners.forEach((f) => f(v)) } }
  const sayHello = () => { const h = hello(); if (h && ws?.readyState === 1) ws.send(JSON.stringify(h)) }

  const connect = () => {
    if (closed || typeof WebSocket === 'undefined') return
    try { ws = new WebSocket(wsUrl(path)) } catch { schedule(); return }
    ws.onopen = () => { wait = 1000; sayHello() }
    ws.onmessage = (m) => {
      let e: LiveEvent
      try { e = JSON.parse(String(m.data)) } catch { return }
      if (e.type === 'ready') setOpen(true)
      listeners.forEach((f) => f(e))
    }
    ws.onclose = (ev) => {
      setOpen(false); ws = null
      // Refused for good (bad sign-in, not staff, chat switched off, too many): stop trying.
      if (ev.code === 4401 || ev.code === 4403 || ev.code === 4429) { closed = true; return }
      schedule()
    }
    ws.onerror = () => { /* onclose follows */ }
  }
  // Back off to 30s, with a little jitter so a server restart is not met by every tab at once.
  const schedule = () => {
    if (closed || timer) return
    timer = setTimeout(() => { timer = null; connect() }, wait + Math.random() * 500)
    wait = Math.min(wait * 2, 30000)
  }
  // Coming back to a tab or back online: try now rather than at the end of the back-off.
  const nudge = () => { if (!ws && !closed) { if (timer) { clearTimeout(timer); timer = null } wait = 1000; connect() } }
  window.addEventListener('online', nudge)
  document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'visible') nudge() })

  connect()
  return {
    send: (o) => { if (ws?.readyState === 1) ws.send(JSON.stringify(o)) },
    close: () => { closed = true; if (timer) clearTimeout(timer); ws?.close(); window.removeEventListener('online', nudge) },
    isOpen: () => open,
    isClosed: () => closed,
    on: (fn) => { listeners.add(fn); return () => { listeners.delete(fn) } },
    onStatus: (fn) => { statusListeners.add(fn); return () => { statusListeners.delete(fn) } },
    rehello: sayHello,
  }
}

/* ─────────────────────────────── the staff app's one connection ─────────────────────────────── */

let staff: Live | null = null
let signedIn = false

/** Told by the sign-in (lib/auth): the socket's credentials are the protected cookie. */
export function setStaffSignedIn(v: boolean) { signedIn = v; if (!v) closeStaffLive() }

/** The staff connection, opened on first use and shared by every screen. */
export function staffLive(): Live | null {
  if (!signedIn) { staff?.close(); staff = null; return null }
  if (staff) return staff
  // The browser sends the sign-in cookie with the connection; the first frame just says hello.
  staff = connectLive('/api/v1/dashboard/ws', () => ({ type: 'auth' }))
  return staff
}

/** Signing out ends the connection; the next person to sign in gets their own. */
export function closeStaffLive() { staff?.close(); staff = null }

/** Is the staff connection up? For choosing how often to poll. */
export function useStaffLive(): [Live | null, boolean] {
  const [live] = useState(() => staffLive())
  const [up, setUp] = useState(() => !!live?.isOpen())
  useEffect(() => (live ? live.onStatus(setUp) : undefined), [live])
  return [live, up]
}
